import { useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { GET_BOTMAKER_SETTINGS } from '../../../gql/queries/botmaker'
import { Button, Form, Modal } from 'react-bootstrap'
import { CampaignChannel, IMessageTemplate } from '../../../types/campaigns'
import { SUPPORTED_CHANNELS } from '../../../constants/campaigns'
import { TemplateStarter } from '../../../constants/templateStarters'

type TemplateFormValues = {
  name: string
  description?: string
  channel: CampaignChannel
  subject?: string
  body: string
  variables?: string[]
  metadata?: Record<string, any>
  namespace?: string
  enabledChannels?: CampaignChannel[]
}

type Props = {
  show: boolean
  onHide: () => void
  onSubmit: (values: TemplateFormValues) => Promise<void>
  template?: IMessageTemplate | null
  starter?: TemplateStarter | null
  loading?: boolean
  defaultChannel?: CampaignChannel
}



const channelOptions = SUPPORTED_CHANNELS

const parseTemplateMetadata = (metadata: unknown): Record<string, any> => {
  if (!metadata) return {}
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata)
    } catch (e) {
      console.error('Failed to parse metadata JSON', e)
      return {}
    }
  }
  if (typeof metadata === 'object') {
    return metadata as Record<string, any>
  }
  return {}
}

export const TemplateFormModal = ({ show, onHide, onSubmit, template, starter, loading, defaultChannel }: Props) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [channel, setChannel] = useState<CampaignChannel>('EMAIL')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [variables, setVariables] = useState('')
  const [metadataJson, setMetadataJson] = useState('{}')
  const [namespace, setNamespace] = useState('')

  const { data: settingsData } = useQuery(GET_BOTMAKER_SETTINGS, {
    skip: channel !== 'WHATSAPP',
  })

  // Determine initial enabled channels from template
  const [enabledChannels, setEnabledChannels] = useState<CampaignChannel[]>([])

  useEffect(() => {
    if (settingsData?.botmakerSettings?.namespace && !template) {
      setNamespace(settingsData.botmakerSettings.namespace)
    }
  }, [settingsData, template])

  useEffect(() => {
    if (!template) {
      const initialChannel = starter?.channel || defaultChannel || 'EMAIL'
      setName('')
      setDescription('')
      setChannel(initialChannel)
      setSubject('')
      setBody('')
      setVariables('')
      setMetadataJson('{}')
      setNamespace('')
      setEnabledChannels([initialChannel])
      return
    }
    setName(template.name)
    setDescription(template.description ?? '')
    setChannel(template.channel)
    setSubject(template.subject ?? '')
    setBody(template.body)
    setVariables(
      Array.isArray(template.variables)
        ? template.variables.join(', ')
        : (template.variables || '')
    )
    const meta = parseTemplateMetadata(template.metadata)

    setMetadataJson(JSON.stringify(meta ?? {}, null, 2))
    setNamespace(meta?.namespace || '')

    // Check metadata for enabled channels, fallback to legacy channel or just EMAIL
    const metaChannels = meta?.enabled_channels
    if (Array.isArray(metaChannels)) {
      setEnabledChannels(metaChannels)
    } else {
      setEnabledChannels(template.channel ? [template.channel] : ['EMAIL'])
    }
  }, [template, defaultChannel])

  // Pre-fill form with starter content
  useEffect(() => {
    if (starter && !template) {
      setName(starter.name)
      setDescription(starter.description)
      setChannel(starter.channel)
      setSubject(starter.content.subject)
      setBody(starter.content.bodyText)
      setVariables(starter.content.variables.join(', '))
      setEnabledChannels([starter.channel])
    }
  }, [starter, template])

  const handleChannelToggle = (ch: CampaignChannel) => {
    setEnabledChannels(prev =>
      prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
    )
  }

  const handleSubmit = async () => {
    const trimmedName = name.trim()
    const trimmedDescription = description.trim()
    const trimmedSubject = subject.trim()
    const parsedVariables = variables
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)

    const currentMetadata = parseTemplateMetadata(metadataJson)
    const nextMetadata: Record<string, any> = {
      ...currentMetadata,
      enabled_channels: enabledChannels,
    }

    if (namespace.trim()) {
      nextMetadata.namespace = namespace.trim()
    } else {
      delete nextMetadata.namespace
    }

    if (starter && !template) {
      nextMetadata.starter_id = starter.id
      nextMetadata.starter_preview_html = starter.content.bodyHtml
    }

    const payload: TemplateFormValues = {
      name: trimmedName,
      description: trimmedDescription || undefined,
      channel: enabledChannels[0] || channel,
      subject: trimmedSubject || undefined,
      body,
      variables: parsedVariables,
      enabledChannels, // Explicit argument for backend mutation
      metadata: nextMetadata,
    }

    await onSubmit(payload)
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{template ? 'Edit template' : 'Create template'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className='mb-4'>
          <Form.Label>Name</Form.Label>
          <Form.Control
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder='e.g. Renewal Reminder'
            autoFocus
          />
        </Form.Group>

        <Form.Group className='mb-4'>
          <Form.Label>Enabled Channels</Form.Label>
          <div className='d-flex gap-3 flex-wrap'>
            {channelOptions.map(opt => (
              <Form.Check
                key={opt}
                type="checkbox"
                id={`channel-${opt}`}
                label={opt}
                checked={enabledChannels.includes(opt)}
                onChange={() => handleChannelToggle(opt)}
              />
            ))}
          </div>
          <Form.Text muted>Select which channels you want to configure for this template.</Form.Text>
        </Form.Group>

        <Form.Group className='mb-4'>
          <Form.Label>Subject</Form.Label>
          <Form.Control
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder='e.g. Important notice about your policy'
          />
          <Form.Text muted>
            Default subject for email variants.
          </Form.Text>
        </Form.Group>

        <Form.Group className='mb-4'>
          <Form.Label>Description</Form.Label>
          <Form.Control
            as='textarea'
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleSubmit}
          disabled={loading || !name.trim() || enabledChannels.length === 0}
        >
          {template ? 'Save' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
